<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pb-0">
        <h1 class="tw-text-2xl sm:tw-text-3xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Support</h1>
        <div class="tw-text-gray-600">Do you have a question about your plan? — Reach out below.</div>
        <base-tabs v-if="supportInfo.length > 0" class="tw-mt-2 tw-pb-4 md:tw-pb-0 tw-mb-0" no-border @click="tabClick($event)">
          <base-tab v-for="business in supportInfo" :key="business.businessId" :selected="selectedBusinessId === business.businessId" :title="business.businessName"></base-tab>
        </base-tabs>
      </base-content>
    </div>
    <base-content>
      <base-card>
        <ul class="tw-divide-y tw-divide-gray-200 tw-mb-0 tw-space-y-4" v-if="isLoading">
          <li v-for="i in 3" :key="i" :class="[{ 'tw-pt-4': i - 1 }, 'tw-pb-0.5']">
            <base-skeleton-loader class="tw-h-3 tw-w-36 tw-mt-1.5" />
            <div class="tw-flex tw-items-center tw-mt-3 tw-mb-4.5">
              <base-skeleton-loader class="tw-h-2 tw-w-28" />
              <base-skeleton-loader class="tw-h-2 tw-w-28 tw-ml-3" />
            </div>
          </li>
        </ul>
        <div v-else>
          <div class="tw-mt-4 tw-flex-col" v-if="selectedBusiness.businessName === 'Rx Harmony'">
            <div class="tw-text-gray-500">Messages are currently unavailable. To contact us, please email or call using the contact information below.</div>
          </div>
          <div class="tw-mt-4 tw-flex-col" v-else-if="supportInfo.length === 0">
            <div class="tw-text-gray-500">You do not have any plans or prescriptions with any groups yet.</div>
          </div>
          <div class="tw-mt-4 tw-flex-col" v-else>
            <base-textarea label="Send us a message" v-model="messages['message' + selectedBusiness.businessId]" />
            <div class="tw-mt-3 tw-w-100 tw-flex tw-justify-end">
              <base-button color="primary" size="sm" :loading="isSaving" @click="sendNewMessage(selectedBusiness.businessId)">Send</base-button>
            </div>
          </div>
          <div v-if="supportInfo.length > 0" class="tw-flex-col tw-mt-5">
            <div class="tw-mt-4 tw-font-normal tw-text-base">Contact Information</div>
            <div class="tw-flex tw-mt-2">
              <div class="tw-text-gray-700 tw-flex tw-items-center tw-text-sm tw-leading-none">
                <mail-icon class="tw-h-3.5 tw-w-3.5" />
                <a :href="`mailto:${selectedBusiness.businessEmail}`" class="tw-ml-1.5 tw-text-gray-700 hover:tw-text-gray-500">{{ selectedBusiness.businessEmail }}</a>
              </div>
              <div class="tw-text-gray-700 tw-flex tw-items-center tw-text-sm tw-leading-none tw-ml-3">
                <phone-icon class="tw-h-3.5 tw-w-3.5" />
                <base-phone-number-field class="tw-ml-1.5" :value="selectedBusiness.businessPhone" />
              </div>
            </div>
          </div>
        </div>
      </base-card>
      <base-card v-if="selectedBusiness.useTodoSetting === true && selectedBusiness.cases !== null" class="tw-mt-3">
        <div class="tw-text-md sm:tw-text-lg tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Support Cases</div>
        <base-list-group class="tw-mt-3">
          <base-list-item v-for="(supportCase, index) in selectedBusiness.cases" :key="supportCase.todoItemId" :showBottomBorder="index != selectedBusiness.cases.length - 1">
            <div class="tw-flex tw-justify-between">
              <div class="tw-flex-col">
                <div class="tw-text-md tw-font-semibold tw-text-gray-700">Online Support Question</div>
                <div class="tw-text-md tw-font-normal tw-text-gray-600 tw-mt-1">{{ supportCase.notes[0].value }}</div>
                <div class="tw-text-xs tw-font-light tw-text-gray-600 tw-mt-1">{{ customDateFormatter(supportCase.insertDate) }}</div>
              </div>
              <div class="tw-my-auto">
                <base-button @click="showMessages(supportCase)" type="flat" size="sm" class="tw-hidden sm:tw-flex">
                  <template v-slot:icon>
                    <comment-icon />
                  </template>
                  Messages
                </base-button>
                <base-button @click="showMessages(supportCase)" type="flat" size="sm" class="sm:tw-hidden tw-flex">
                  <template v-slot:icon>
                    <comment-icon />
                  </template>
                </base-button>
              </div>
            </div>
          </base-list-item>
        </base-list-group>
      </base-card>
    </base-content>
    <messages-slideover :userId="id" ref="messagesSlideover" @sendMessage="sendChat"></messages-slideover>
  </div>
</template>

<script>
import { MailIcon, PhoneIcon, CommentIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import MessagesSlideover from "@/views/user/support/components/MessagesSlideover";

export default {
  name: "UserSupport",
  components: {
    MailIcon,
    PhoneIcon,
    CommentIcon,
    MessagesSlideover,
  },
  data() {
    return {
      isLoading: true,
      supportInfo: [],
      messages: {},
      isSaving: false,
      selectedBusinessId: null,
    };
  },
  computed: {
    ...mapGetters("cognito", ["id"]),
    selectedBusiness() {
      if (this.selectedBusinessId !== null) {
        return this.supportInfo.find((obj) => {
          return obj.businessId === this.selectedBusinessId;
        });
      } else return { businessId: null, businessName: null, useTodoSetting: false, businessEmail: null, businessPhone: null, questions: [] };
    },
  },
  created() {
    this.getSupportInfo();
  },
  methods: {
    customDateFormatter(date) {
      return moment
        .utc(date)
        .local()
        .format("dddd, MMM DD hh:mma");
    },
    showMessages(supportCase) {
      this.$refs.messagesSlideover.show(this.selectedBusinessId, supportCase.todoItemId);
    },
    tabClick(event) {
      this.selectedBusinessId = this.supportInfo.find((obj) => {
        return obj.businessName === event;
      }).businessId;
    },
    getSupportInfo() {
      this.isLoading = true;

      this.$axios
        .get(`/users/${this.id}/support-info`)
        .then((response) => {
          this.supportInfo = response.data;
          if (this.selectedBusinessId == null && response.data.length > 0) this.selectedBusinessId = response.data[0].businessId;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    sendChat(event) {
      this.messages["message" + this.selectedBusinessId] = event.message;
      this.sendReplyMessage(event.todoItemId, event.message);
    },
    sendNewMessage(businessId) {
      this.isSaving = true;

      this.$axios
        .post(`/users/${this.id}/businesses/${businessId}/support-questions`, { message: this.messages["message" + businessId] })
        .then(() => {
          this.getSupportInfo();
          this.messages = {};
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Message sent", message: `Your message has been sent to ${this.selectedBusiness.businessName} successfully.` });
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to send message",
            message:
              "Sorry, we had a problem sending this message. Please reload the page to try again. If this problem persists, use the contact information on this page to get in touch.",
            error: err,
          });
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    sendReplyMessage(todoItemId, message) {
      this.$axios.post(`/businesses/${this.selectedBusinessId}/todo/${todoItemId}/notes`, { value: message }).catch((err) => {
        this.errorTitle = `Error saving note`;
        this.errorMessage = "Sorry, we had trouble adding this note. The technicians have been notified. Please try again.";
        this.error = err;
      });
    },
  },
};
</script>
